import React, { useEffect, useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import "./BookingFlowDemo.css";

const WEEKDAYS = {
  en: ["S", "M", "T", "W", "T", "F", "S"],
  ar: ["ح", "ن", "ث", "ر", "خ", "ج", "س"],
};

const MONTHS = {
  en: "June 2025",
  ar: "يونيو 2025",
};

const SLOTS = {
  en: [
    { id: "10", label: "10:00 AM", booked: false },
    { id: "1130", label: "11:30 AM", booked: true },
    { id: "1430", label: "2:30 PM", booked: false, pick: true },
    { id: "1600", label: "4:00 PM", booked: false },
    { id: "1730", label: "5:30 PM", booked: true },
    { id: "1900", label: "7:00 PM", booked: false },
  ],
  ar: [
    { id: "10", label: "10:00 ص", booked: false },
    { id: "1130", label: "11:30 ص", booked: true },
    { id: "1430", label: "2:30 م", booked: false, pick: true },
    { id: "1600", label: "4:00 م", booked: false },
    { id: "1730", label: "5:30 م", booked: true },
    { id: "1900", label: "7:00 م", booked: false },
  ],
};

/** Compact calendar cells for June 2025 — day 15 is the animated pick. */
const CALENDAR_DAYS = [
  null, null, null, null, null, null, 1,
  2, 3, 4, 5, 6, 7, 8,
  9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 19, 20, 21, 22,
  23, 24, 25, 26, 27, 28, 29,
  30,
];

const COPY = {
  en: {
    calendar: "Pick a day",
    slots: "Pick a time",
    confirmed: "Booking confirmed!",
    summary: "Sat 15 Jun · 2:30 PM",
  },
  ar: {
    calendar: "اختر اليوم",
    slots: "اختر الوقت",
    confirmed: "تم تأكيد الحجز!",
    summary: "السبت 15 يونيو · 2:30 م",
  },
};

export default function BookingFlowDemo({ lang = "en", featured = false }) {
  const rootRef = useRef(null);
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | day | slot | confirmed

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!active) {
      setPhase("idle");
      return undefined;
    }

    let cancelled = false;
    const timers = [];

    const run = () => {
      if (cancelled) return;
      setPhase("idle");
      timers.push(setTimeout(() => !cancelled && setPhase("day"), 600));
      timers.push(setTimeout(() => !cancelled && setPhase("slot"), 2200));
      timers.push(setTimeout(() => !cancelled && setPhase("confirmed"), 3800));
      timers.push(setTimeout(() => !cancelled && run(), 6200));
    };

    run();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [active]);

  const copy = COPY[lang] || COPY.en;
  const weekdays = WEEKDAYS[lang] || WEEKDAYS.en;
  const slots = SLOTS[lang] || SLOTS.en;
  const daySelected = phase === "day" || phase === "slot" || phase === "confirmed";
  const slotSelected = phase === "slot" || phase === "confirmed";
  const showConfirm = phase === "confirmed";

  return (
    <div
      ref={rootRef}
      className={`bfd-demo${featured ? " bfd-demo--featured" : ""}${active ? " bfd-demo--active" : ""}${showConfirm ? " bfd-demo--confirmed" : ""}`}
      data-phase={phase}
      aria-hidden="true"
    >
      <div className="bfd-stage">
        <div className="bfd-panel bfd-panel--calendar">
          <p className="bfd-panel-label">{copy.calendar}</p>
          <div className="bfd-calendar">
            <div className="bfd-cal-header">{MONTHS[lang] || MONTHS.en}</div>
            <div className="bfd-cal-weekdays">
              {weekdays.map((d, i) => (
                <span key={`${d}-${i}`}>{d}</span>
              ))}
            </div>
            <div className="bfd-cal-grid">
              {CALENDAR_DAYS.map((day, i) =>
                day == null ? (
                  <span key={`empty-${i}`} className="bfd-cal-cell bfd-cal-cell--empty" />
                ) : (
                  <span
                    key={day}
                    className={[
                      "bfd-cal-cell",
                      day === 15 ? "bfd-cal-cell--target" : "",
                      day === 15 && phase === "day" && !daySelected ? "bfd-cal-cell--pulse" : "",
                      day === 15 && daySelected ? "bfd-cal-cell--selected" : "",
                      day < 15 ? "bfd-cal-cell--past" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {day}
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        <div className="bfd-panel bfd-panel--slots">
          <p className="bfd-panel-label">{copy.slots}</p>
          <div className={`bfd-slots${daySelected ? " bfd-slots--enabled" : ""}`}>
            {slots.map((slot) => (
              <span
                key={slot.id}
                className={[
                  "bfd-slot",
                  slot.booked ? "bfd-slot--booked" : "",
                  slot.pick && slotSelected ? "bfd-slot--selected" : "",
                  slot.pick && phase === "slot" && !slotSelected ? "bfd-slot--pulse" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {slot.label}
              </span>
            ))}
          </div>
        </div>

        <div className={`bfd-confirm${showConfirm ? " bfd-confirm--show" : ""}`}>
          <span className="bfd-confirm-icon">
            <FaCheck aria-hidden="true" />
          </span>
          <strong className="bfd-confirm-title">{copy.confirmed}</strong>
          <span className="bfd-confirm-summary">{copy.summary}</span>
        </div>
      </div>
    </div>
  );
}
